import { useQuery } from "@tanstack/react-query";

import useUser from "@/api/apis";
import type { MyCoupon } from "@/libs/coupon/issued-coupon";

/**
 * 로그인 회원의 발급 쿠폰 목록 (mgmt `GET /api/v1/coupons/me`, `@IsUser`).
 *
 * 브라우저에서 mgmt 를 직접 부르지 않고 `/admin-api/*` 리라이트 프록시를 쓴다(CartApi 와 동일 패턴).
 *
 * 게스트는 조회하지 않는다 — 쿠폰은 회원에게만 발급되고 소유 판정이 uid 기반이다.
 * 사용/만료분도 함께 오므로 노출 필터는 소비처가 [isUsable] 로 정한다.
 */
export function useMyCoupons() {
  const { token } = useUser();
  const accessToken = token?.token;

  return useQuery<MyCoupon[]>({
    queryKey: ["my-coupons", accessToken ? "auth" : "guest"],
    enabled: !!accessToken,
    // 쿠폰은 적용 직전에 목록이 최신이어야 한다(만료·사용 반영). 짧게 잡는다.
    staleTime: 30_000,
    queryFn: async () => {
      const res = await fetch("/admin-api/api/v1/coupons/me/", {
        headers: { authorization: "Bearer " + accessToken },
      });
      if (!res.ok) {
        // 쿠폰 조회 실패가 체크아웃을 막아선 안 된다 — 빈 목록으로 degrade 한다.
        // (throw 하면 react-query 가 캐시된 이전 값을 보존해 오히려 stale 을 보여줄 수 있다.)
        return [];
      }
      const json = await res.json();
      const list = json?.message;
      return Array.isArray(list) ? (list as MyCoupon[]) : [];
    },
  });
}
